/**
 * LogicTrade Configurator Custom Element
 * Uses LogicTrade Configurator Core API (https://configurator.logictrade.app and https://configurator.logictrade.app/swagger/index.html)
 */

import {
  addConfiguredProductToCart,
  capturePreview,
  createConfiguredProduct,
  findViewer,
  resolveApiUrl,
} from "./henk-configurator-product.js";

const API_BASE = "https://configurator.logictrade.app";

/**
 * Vertaalde UI-teksten uit snippets/translations.liquid (window.translations).
 * De sleutels zijn identiek aan de locale-keys, dus t("buttons.add_to_cart")
 * geeft hetzelfde als {{ 'buttons.add_to_cart' | t }} in Liquid.
 */
function t(key) {
  return (
    key
      .split(".")
      .reduce((value, part) => (value == null ? value : value[part]), window.translations) ?? ""
  );
}

// De placeholdervariant "Samengesteld" is niet meer nodig: er wordt per
// configuratie een echt product aangemaakt (zie henk-configurator-product.js).

// Prijzen in de configurator altijd in Nederlandse notatie (€ 1.234,56),
// onafhankelijk van de browsertaal van de bezoeker — anders kreeg een
// bezoeker met een Engelse browser €1,234.56 te zien.
const PRICE_LOCALE = "nl-NL";
const PRICE_CURRENCY = "EUR";

function formatPrice(euros, options = {}) {
  if (euros == null || euros === "") return "";
  const value = Number(euros);
  if (!Number.isFinite(value)) return "";
  return new Intl.NumberFormat(PRICE_LOCALE, {
    style: "currency",
    currency: PRICE_CURRENCY,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    ...options,
  }).format(value);
}

// PRODUCT_TYPE_MAP en buildProductTitle staan nu op de server
// (src/lt/constants.ts en src/lt/normalize.ts), zodat de titel van het
// aangemaakte product en de titel in de winkelwagen niet uiteen kunnen lopen.

customElements.define(
  "logic-configurator",
  class LogicConfigurator extends HTMLElement {
    constructor() {
      super();
      this.configurationGuid = null;
      this.configuration = null;
      this._abortController = null;
      this._activeStepIndex = 0;
    }

    static get observedAttributes() {
      return [
        "data-lt-token",
        "data-lt-product",
        "data-lt-configuration-id",
        "data-shopify-variant",
        "data-shopify-product-title",
        "data-material-finishes",
        "data-lt-preview-mode",
        "data-configurator-api",
      ];
    }

    get previewMode() {
      return (
        this.dataset.ltPreviewMode === "true" ||
        this.dataset.ltPreviewMode === "1"
      );
    }

    get configuratorImages() {
      try {
        const raw = this.dataset.configuratorImages;
        if (!raw) return [];
        return JSON.parse(raw);
      } catch {
        return [];
      }
    }

    get materialFinishes() {
      try {
        const raw = this.dataset.materialFinishes;
        if (!raw) return [];
        return JSON.parse(raw);
      } catch {
        return [];
      }
    }

    get ltCfgOptions() {
      try {
        const raw = this.dataset.ltCfgOptions;
        if (!raw) return {};
        return JSON.parse(raw);
      } catch {
        return {};
      }
    }

    get ltTexts() {
      const raw = this.dataset.ltTexts || "";
      if (raw !== this._ltTextsRaw) {
        this._ltTextsRaw = raw;
        try {
          this._ltTexts = raw ? JSON.parse(raw) : {};
        } catch {
          this._ltTexts = {};
        }
      }
      return this._ltTexts;
    }

    /**
     * Vertaling van een LogicTrade-tekst uit de `configurator_text`-metaobjects
     * (snippets/configurator-texts.liquid). De eerste kandidaat met een entry
     * wint; zonder entry blijft de Nederlandse tekst van LogicTrade staan.
     */
    _ltText(...candidates) {
      const texts = this.ltTexts;
      for (const candidate of candidates) {
        const key = String(candidate ?? "").trim().toLowerCase();
        if (key && texts[key]) return texts[key];
      }
      return null;
    }

    _stepTitle(step) {
      return (
        this._ltText(step.code, step.description) ||
        step.description ||
        step.code ||
        step.id
      );
    }

    _optionLabel(step, opt, code) {
      const text = opt.value || opt.key || opt.label;
      const stepCode = step.code ? `${step.code}.${code}` : null;
      return this._ltText(stepCode, text, code) || text;
    }

    get token() {
      return this.dataset.ltToken || "";
    }

    get articleCode() {
      return this.dataset.ltProduct || "";
    }

    get configurationHeaderId() {
      const id = this.dataset.ltConfigurationId;
      return id ? parseInt(id, 10) : null;
    }

    get shopifyVariantId() {
      return this.dataset.shopifyVariant || "";
    }

    get shopifyProductTitle() {
      return this.dataset.shopifyProductTitle || "";
    }

    connectedCallback() {
      this._abortController = new AbortController();
      this._ensureElements();
      this._bindEvents();
      this._start();
    }

    disconnectedCallback() {
      this._abortController?.abort();
    }

    _ensureElements() {
      if (!this._modesEl) {
        this._modesEl =
          this.querySelector("#configurator-modes") || this._createModes();
      }
      if (!this._stepsEl) {
        this._stepsEl =
          this.querySelector("#configurator-steps") || this._createSteps();
      }
      if (!this._priceEl) {
        this._priceEl =
          this.querySelector("#configurator-price") || this._createPrice();
      }
      if (!this._addToCartBtn) {
        this._addToCartBtn =
          this.querySelector("#configurator-add-to-cart") ||
          this._createAddToCart();
      }
    }

    _createModes() {
      return (
        this.querySelector("#configurator-modes") ||
        document.createElement("div")
      );
    }

    _createSteps() {
      const wrap =
        this.querySelector(".configurator__steps") ||
        document.createElement("div");
      wrap.className = "configurator__steps";
      wrap.id = "configurator-steps";
      if (!this.contains(wrap)) this.appendChild(wrap);
      return wrap;
    }

    _createPrice() {
      const wrap =
        this.querySelector(".configurator__summary") ||
        document.createElement("div");
      wrap.className = "configurator__summary";
      const priceWrap = document.createElement("div");
      priceWrap.className = "configurator__price";
      const span = document.createElement("span");
      span.id = "configurator-price";
      priceWrap.appendChild(span);
      wrap.appendChild(priceWrap);
      const btn = document.createElement("button");
      btn.id = "configurator-add-to-cart";
      btn.className = "button button--primary";
      btn.type = "button";
      btn.textContent = t("buttons.add_to_cart");
      wrap.appendChild(btn);
      if (!this.contains(wrap)) this.appendChild(wrap);
      return span;
    }

    _createAddToCart() {
      return this.querySelector("#configurator-add-to-cart");
    }

    _bindEvents() {
      const btn = this.querySelector("#configurator-add-to-cart");
      if (btn && !btn._bound) {
        btn._bound = true;
        btn.addEventListener("click", () => this._addToCart());
      }
    }

    _emit(name, detail = {}) {
      this.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
    }

    async _start() {
      if (!this.token || !this.articleCode) {
        this._emit("configurator-error", {
          message: "Missing token or product code",
        });
        return;
      }

      this._setLoading(true);
      try {
        // Geen `language`: dat kostte ~3 seconden per start. LogicTrade levert
        // Nederlands, de vertaling loopt via Shopify (zie _ltText).
        const body = {
          token: this.token,
          configurationOptions: {
            articleCode: this.articleCode,
          },
          showOptions: {
            hideEmptySteps: true,
            hideStepsWithOneAnswer: true,
            hideNonWebshopSteps: true,
          }
        };
        if (this.configurationHeaderId) {
          body.configurationOptions.configurationHeaderId =
            this.configurationHeaderId;
        }

        const res = await fetch(`${API_BASE}/api/logictrade/start`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
          signal: this._abortController?.signal,
        });

        if (!res.ok) {
          const text = await res.text();
          throw new Error(`Start failed: ${res.status} ${text}`);
        }

        const data = await res.json();
        this._applyConfiguration(data);
      } catch (err) {
        if (err.name === "AbortError") return;
        this._emit("configurator-error", { error: err, message: err.message });
        console.error("[logic-configurator]", err);
      } finally {
        this._setLoading(false);
      }
    }

    async ltAnswer(stepId, value) {
      if (!this.configurationGuid) return;
      this._setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/api/logictrade/answer`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            configurationGuid: this.configurationGuid,
            stepId,
            value,
          }),
          signal: this._abortController?.signal,
        });

        if (!res.ok) {
          const text = await res.text();
          throw new Error(`Answer failed: ${res.status} ${text}`);
        }

        const data = await res.json();
        this._applyConfiguration(data);
      } catch (err) {
        if (err.name === "AbortError") return;
        this._emit("configurator-error", { error: err, message: err.message });
        console.error("[logic-configurator]", err);
      } finally {
        this._setLoading(false);
      }
    }

    async ltFinish() {
      if (!this.configurationGuid) return null;
      try {
        const res = await fetch(`${API_BASE}/api/logictrade/finish`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ configurationGuid: this.configurationGuid }),
          signal: this._abortController?.signal,
        });

        if (!res.ok) {
          const text = await res.text();
          throw new Error(`Finish failed: ${res.status} ${text}`);
        }

        const data = await res.json();
        this._applyConfiguration(data);
        return data;
      } catch (err) {
        if (err.name === "AbortError") return null;
        this._emit("configurator-error", { error: err, message: err.message });
        console.error("[logic-configurator]", err);
        return null;
      }
    }

    _applyConfiguration(data) {
      if (data.configurationGuid)
        this.configurationGuid = data.configurationGuid;
      this.configuration = data;

      if (data.errors && data.errors.length > 0) {
        const msg = data.errors.map((e) => e.message || e).join("; ");
        this._emit("configurator-error", { message: msg, errors: data.errors });
      }

      this._logState(data.steps || []);
      this._renderSteps(data.steps || []);
      this._renderPreview(data.image);
      this._renderPrice(data);
    }

    _logState(steps) {
      const state = {};
      steps.forEach((s) => {
        const selected =
          s.selectedValue ??
          s.possibleValues?.find((v) => (v.key || v.value) === s.value);
        state[s.code || s.id] =
          selected?.code ?? selected?.key ?? s.value ?? null;
      });

      const shape = state["SHAPE"];
      const edgetype = state["EDGETYPE"];
      const thickness = state["THICKNESS"];
      const frame = state["FRAME"];
      const topFinish = state["TOP_FINISH"];
      const frmFinish = state["FRAME_FINISH"];

      state.articleCode = this.articleCode.toLowerCase();
      const prefix = state.articleCode ? `${state.articleCode}-` : "";

      // Bepaal welke bestanden beschikbaar zijn in de viewer
      const viewer = document.querySelector("studio-henk-3d-viewer") || document.querySelector("salontafel-3d-viewer");
      const viewerFiles = (() => {
        try { return JSON.parse(viewer?.dataset?.files || "[]"); } catch { return []; }
      })();
      const hasGlb = viewerFiles.some(f => {
        const ext = (f.filename || f.name || "").split(".").pop().toLowerCase();
        return ext === "glb" || ext === "gltf" || f.format === "glb";
      });

      const files = [];
      if (hasGlb) {
        if (shape && edgetype) {
          const t = thickness ? `-${thickness}` : "";
          files.push(`${prefix}${shape}${t}-${edgetype}.glb`);
        }
        if (frame) files.push(`${prefix}${frame}.glb`);
      } else {
        if (topFinish) files.push(`${prefix}${topFinish}.png`);
        if (frmFinish) files.push(`${prefix}${frmFinish}.png`);
      }

      window._ltState = state;
      window.dispatchEvent(
        new CustomEvent("lt-state-update", { detail: state }),
      );

      console.group("[logic-configurator] state update");
      console.table(
        Object.entries(state).map(([code, val]) => ({
          stepCode: code,
          selectedCode: val,
        })),
      );
      console.log(`Viewer modus: ${hasGlb ? "3D (GLB)" : "afbeeldingen"}`);
      if (files.length) {
        console.log(hasGlb ? "3D bestanden:" : "Afbeeldingen:", files);
      } else {
        console.log("Geen bestandsmapping gevonden (controleer step codes hierboven)");
      }
      console.groupEnd();
    }

    _findMaterialFinishMatch(optionValue, optionCode, silenceWarning) {
      const finishes = this.materialFinishes;
      if (!finishes?.length) return null;

      const norm = (s) => (s || "").toLowerCase().trim();
      const valueCode = norm(optionValue);
      const fallbackCode = norm(optionCode);
      const candidateCodes = [valueCode, fallbackCode].filter(Boolean);
      if (!candidateCodes.length) return null;

      for (const mf of finishes) {
        const finishCode = norm(mf.code);
        if (finishCode && candidateCodes.includes(finishCode)) return mf;
      }

      console.log('silenceWarning', silenceWarning);

      if (silenceWarning === true) {
      } else {
        console.warn('[finish-match] geen match voor:', { optionValue, optionCode, candidateCodes, beschikbareCodes: finishes.map(f => f.code) });
      }

      return null;
    }

    _findCfgOptionMatch(step, optKey, optCode) {
      const allOptions = this.ltCfgOptions;
      // const stepCode = (step.code || '').toUpperCase();
      // const resolvedCode = stepCode === 'FRAME_GROUP' ? 'frame' : (step.code || '').toLowerCase();
      // const typeHandle = 'lt_cfg_' + resolvedCode;
      const typeHandle = 'lt_cfg_' + (step.code || '').toLowerCase();
      const entries = allOptions[typeHandle];
      if (!entries?.length) return null;

      const norm = (s) => (s || '').toLowerCase().trim();
      const candidates = [norm(optKey), norm(optCode)].filter(Boolean);

      for (const entry of entries) {
        if (candidates.includes(norm(entry.code))) return entry;
      }
      return null;
    }

    _isStofStep(step, options) {
      // const s = (step.description || step.code || step.id || "").toLowerCase();
      // If two or more options match by data-code then it is safe to assume we are dealing with a stof step.
      const matchingOptions = options.filter(opt => {
        const key = opt.key || opt.value;
        const code = opt.code ?? opt.key ?? opt.value ?? opt.attributes?.code ?? "";

        const match = this._findMaterialFinishMatch(key, code, true);
        return !!match;
      });

      return matchingOptions?.length >= 2;
    }

    _isWebshopEnabled(...values) {
      for (const value of values) {
        if (value === true || value === 1) return true;
        if (typeof value === "string") {
          const normalized = value.trim().toLowerCase();
          if (
            normalized === "true" ||
            normalized === "1" ||
            normalized === "yes" ||
            normalized === "ja"
          ) {
            return true;
          }
        }
      }
      return false;
    }

    _shouldShowStofOption(match) {
      // If no material finishes are configured, keep current behavior and show all options.
      if (!this.materialFinishes?.length) return true;
      return !!match;
    }

    _createOptionBtn(step, opt, selectedKey, hasImage, match) {
      const key = opt.key || opt.value;
      const code = opt.code ?? opt.key ?? opt.value ?? opt.attributes?.code ?? "";
      const label = match?.name || this._optionLabel(step, opt, code);

      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "configurator__option henk-button";
      btn.dataset.stepId = step.id;
      btn.dataset.value = key;
      btn.dataset.code = code;

      if (hasImage && match?.imageUrl) {
        const optionInner = document.createElement("span");
        optionInner.className = "configurator__option-inner";
        const img = document.createElement("img");
        img.src = match.imageUrl;
        img.alt = "";
        img.className = "configurator__option-image";
        optionInner.appendChild(img);
        const labelSpan = document.createElement("span");
        labelSpan.className = "configurator__option-label";
        labelSpan.textContent = label;
        optionInner.appendChild(labelSpan);
        btn.appendChild(optionInner);
      } else {
        btn.textContent = label;
      }

      const isSelected = key === selectedKey;
      btn.setAttribute("aria-pressed", isSelected ? "true" : "false");
      if (isSelected) btn.classList.add("is-selected");
      if (opt.enabled === false) {
        btn.disabled = true;
        btn.classList.add("is-disabled");
      }

      btn.addEventListener("click", () => {
        if (opt.enabled === false) return;
        this.ltAnswer(step.id, key);
      });

      return btn;
    }

    _getSelectedLabel(step, options, selectedKey, isStof) {
      const opt =
        options.find((o) => (o.key || o.value) === selectedKey) ??
        step.selectedValue ??
        null;
      if (!opt) {
        return (
          this._ltText(step.valueDescription) || step.valueDescription || ""
        );
      }

      const key = opt.key || opt.value;
      const code = opt.code ?? opt.key ?? opt.value ?? opt.attributes?.code ?? "";
      const cfgMatch = this._findCfgOptionMatch(step, key, code);

      let match = cfgMatch;
      if (isStof) {
        const finishMatch = this._findMaterialFinishMatch(key, code, true);
        match = cfgMatch?.imageUrl ? cfgMatch : finishMatch;
      }

      return (
        match?.name || this._optionLabel(step, opt, code) || step.valueDescription || ""
      );
    }

    _renderSteps(steps) {
      const container =
        this._stepsEl || this.querySelector("#configurator-steps");
      if (!container) return;
      container.innerHTML = "";

      const visibleSteps = steps.filter((s) => {
        if (s.show === false || s.enabled === false) return false;
        const options = s.possibleValues || [];
        if (options.length === 0) return false;
        if (options.length === 1) return false;
        return true;
      });

      visibleSteps.forEach((step) => {
        const group = document.createElement("div");
        group.className = "configurator__step";

        const title = document.createElement("h3");
        title.textContent = this._stepTitle(step);
        // group.appendChild(title);

        const details = document.createElement("details");
        details.open = false;
        details.className =
          "configurator__step-details henk-details henk-details--plusmin";
        details.name = "cf-steps";
        const options = step.possibleValues || [];
        const selectedKey =
          step.selectedValue?.key || step.selectedValue?.value || step.value;
        const isStof = this._isStofStep(step, options);

        const summary = document.createElement("summary");
        summary.className = "henk-details__summary";

        const summaryTitle = document.createElement("span");
        summaryTitle.className = "configurator__step-title";
        summaryTitle.textContent = `${visibleSteps.indexOf(step) + 1}. ${title.textContent}`;
        summary.appendChild(summaryTitle);

        const selectedLabel = this._getSelectedLabel(step, options, selectedKey, isStof);
        if (selectedLabel) {
          const summaryValue = document.createElement("span");
          summaryValue.className = "configurator__step-selected";
          summaryValue.textContent = selectedLabel;
          summary.appendChild(summaryValue);
        }

        details.appendChild(summary);
        group.appendChild(details);
        const hasCfgOptions = (() => {
          const typeHandle = 'lt_cfg_' + (step.code || '').toLowerCase();
          const entries = this.ltCfgOptions[typeHandle];
          return entries?.length > 0;
        })();

        if (isStof) {
          const grouped = new Map();

          options.forEach((opt) => {
            if (opt.show === false) return;
            const key = opt.key || opt.value;
            const code = opt.code ?? opt.key ?? opt.value ?? opt.attributes?.code ?? '';
            const cfgMatch = this._findCfgOptionMatch(step, key, code);
            if (hasCfgOptions && cfgMatch && cfgMatch.webshop === false) return;
            const match = this._findMaterialFinishMatch(key, code, true);
            if (!hasCfgOptions && !this._shouldShowStofOption(match)) return;
            const priceGroupObj = match?.priceGroup ?? null;
            const priceGroupKey = priceGroupObj?.title ?? 0;
            const startingAt = priceGroupObj?.starting_at?.amount ?? null;
            if (!grouped.has(priceGroupKey)) grouped.set(priceGroupKey, { startingAt, items: [] });
            const displayMatch = cfgMatch?.imageUrl ? cfgMatch : match;
            grouped.get(priceGroupKey).items.push({ opt, match: displayMatch });
          });

          [...grouped.entries()]
            .sort(([keyA, { startingAt: saA }], [keyB, { startingAt: saB }]) => {
              // Items zonder priceGroup onderaan
              if (keyA === 0 && keyB !== 0) return 1;
              if (keyB === 0 && keyA !== 0) return -1;
              if (saA === null && saB === null) return 0;
              if (saA === null) return 1;
              if (saB === null) return -1;
              return parseFloat(saA) - parseFloat(saB);
            })
            .forEach(([priceGroupKey, { items }]) => {
              const stepGroup = document.createElement("div");
              const optionsWrap = document.createElement("div");
              const groupTitle = document.createElement('h4');

              if (priceGroupKey) {
                groupTitle.textContent = priceGroupKey;
                groupTitle.className = 'configurator__step-group-title';

                stepGroup.appendChild(groupTitle);
              }

              stepGroup.className = 'configurator__step-group';
              optionsWrap.className = 'configurator__step-options';
              optionsWrap.dataset.priceGroup = priceGroupKey;
              items.forEach(({ opt, match }) => {
                optionsWrap.appendChild(this._createOptionBtn(step, opt, selectedKey, !!match?.imageUrl, match));
              });
              stepGroup.appendChild(optionsWrap);
              details.appendChild(stepGroup);
            });
        } else {
          const optionsWrap = document.createElement("div");
          optionsWrap.className = "configurator__step-options";

          options.forEach((opt) => {
            if (opt.show === false) return;
            const key = opt.key || opt.value;
            const code =
              opt.code ?? opt.key ?? opt.value ?? opt.attributes?.code ?? "";
            const match = this._findCfgOptionMatch(step, key, code);

            if (hasCfgOptions && match && !this._isWebshopEnabled(match.webshop)) return;

            optionsWrap.appendChild(
              this._createOptionBtn(
                step,
                opt,
                selectedKey,
                !!match?.imageUrl,
                match,
              ),
            );
          });
          // group.appendChild(optionsWrap);
          details.appendChild(optionsWrap);
        }

        details.addEventListener("toggle", () => {
          if (details.open) {
            const allDetails = container.querySelectorAll("details");
            this._activeStepIndex = Array.from(allDetails).indexOf(details);
          }
        });

        container.appendChild(group);
      });

      const allDetails = container.querySelectorAll("details");
      const targetIdx = Math.min(this._activeStepIndex, allDetails.length - 1);
      // allDetails[targetIdx]?.setAttribute("open", "");
      // Do not auto-open a dynamic step if static Step 0 (or another step) is already open on load
      const anyOpenOnPage = document.querySelector('details[name="cf-steps"][open]');
      if (!anyOpenOnPage) allDetails[targetIdx]?.setAttribute("open", "");
    }

    _renderPreview(_image) {
      // Afbeelding komt uit data-files via de viewer (lt-state-update), niet uit de LT API
    }

    /** LogicTrade `summary` is vaak een JSON-string met o.a. Price / ActionPrice na finish. */
    _parseLogictradeSummary(data) {
      if (!data?.summary) return null;
      let s = data.summary;
      if (typeof s === "string") {
        try {
          s = JSON.parse(s);
        } catch {
          return null;
        }
      }
      return typeof s === "object" && s !== null ? s : null;
    }

    // De artikelcodes (`_ArticleCodes`, voor de levertijd en de ERP) worden nu
    // door de server uit de LogicTrade-summary gehaald — zie
    // src/lt/normalize.ts (collectArticleCodes) in Configurator-creator.

    /**
     * Prijs in euro's voor UI en winkelmand. Eerst attributes; anders uit geparsede summary (Price, ActionPrice, …).
     */
    _getConfiguratorPriceEuros(data) {
      if (!data) return null;
      const attrs = data.attributes;
      if (attrs && typeof attrs === "object") {
        const direct = attrs.price ?? attrs.Price;
        if (
          direct != null &&
          !Number.isNaN(parseFloat(direct)) &&
          parseFloat(direct) > 0
        ) {
          return parseFloat(direct);
        }
      }
      const summary = this._parseLogictradeSummary(data);
      if (summary) {
        const candidates = [
          summary.Price,
          summary.price,
          summary.ActionPrice,
          summary.actionPrice,
          summary.RecommendedPrice,
          summary.recommendedPrice,
        ];
        for (const c of candidates) {
          if (c != null && !Number.isNaN(parseFloat(c)) && parseFloat(c) > 0) {
            return parseFloat(c);
          }
        }
      }
      return null;
    }

    _renderPrice(data) {
      const el = this._priceEl || this.querySelector("#configurator-price");
      if (!el) return;

      const euros = this._getConfiguratorPriceEuros(data);
      if (euros != null) {
        el.textContent = formatPrice(euros);
        return;
      }
      if (data?.attributes?.formattedPrice) {
        el.textContent = data.attributes.formattedPrice;
      } else {
        el.textContent = "";
      }
    }

    // `spinner: true` zet de knop zelf in loading-state (data-state="loading"
    // verbergt het label en toont een spinner). Dat willen we alleen bij het
    // daadwerkelijk toevoegen aan de cart — bij het wisselen van een optie
    // flikkerde de knop anders bij elke klik. Daar blijft hij alleen even
    // disabled terwijl de nieuwe configuratie wordt opgehaald.
    _setLoading(loading, { spinner = false } = {}) {
      const btn = this.querySelector("#configurator-add-to-cart");
      if (btn) {
        btn.disabled = loading;
        if (loading && spinner) {
          btn.setAttribute("aria-busy", "true");
          btn.dataset.state = "loading";
        } else {
          // henk-button-loading.min.js zet data-state="loading" bij de click;
          // die knop navigeert niet, dus wij moeten hem hier weer opruimen.
          btn.removeAttribute("aria-busy");
          delete btn.dataset.state;
        }
      }
      this.classList.toggle("is-loading", loading);
    }

    _showFinishPreview(data) {
      const el = this.querySelector("#configurator-preview-output");
      if (!el) return;
      el.hidden = false;

      let summaryOutput = data.summary;
      if (typeof summaryOutput === "string") {
        try {
          summaryOutput = JSON.parse(summaryOutput);
        } catch {
          // Keep as string if not valid JSON
        }
      }
      el.textContent =
        typeof summaryOutput === "object"
          ? JSON.stringify(summaryOutput, null, 2)
          : String(summaryOutput ?? "(geen summary)");
    }

    /**
     * Configuratie → geconfigureerd product → winkelwagen.
     *
     * De Configurator Creator-server maakt een echt product aan met de juiste
     * prijs, titel, SKU en afbeelding, en bepaalt welke line item properties
     * er op de regel komen. De Cart Transform is daarmee niet meer nodig.
     */
    async _addToCart() {
      this._setLoading(true, { spinner: true });

      // De drawer gaat direct bij de klik open met de laadstatus erin. Het werk
      // hieronder — ltFinish, screenshot, het product laten aanmaken — duurt
      // een paar seconden; zonder dit zie je tot het eind alleen een knop.
      // HenkCart.add() verderop neemt deze sessie over en speelt hem uit.
      const adding = window.HenkCart?.beginAdding?.() ?? null;

      try {
        const finished = await this.ltFinish();
        if (!finished) {
          adding?.abort();
          return;
        }

        if (this.previewMode) {
          this._showFinishPreview(finished);
        }

        if (!this.configurationGuid) {
          adding?.abort();
          this._emit("configurator-error", {
            message: "Configuratie is niet afgerond (geen configurationGuid)",
          });
          return;
        }

        // De prijs gaat mee als terugval; de server haalt de prijs zelf op bij
        // LogicTrade en die is leidend.
        const priceEuros =
          this._getConfiguratorPriceEuros(finished) ??
          this._getConfiguratorPriceEuros(this.configuration);

        const viewer = findViewer();
        const { screenshot, imageUrl } = capturePreview(viewer);

        const result = await createConfiguredProduct(resolveApiUrl(this), {
          configurationGuid: this.configurationGuid,
          articleCode: this.articleCode,
          configurationId: this.configurationHeaderId,
          title: this.shopifyProductTitle || null,
          price: priceEuros,
          currency: "EUR",
          sourceVariantId: this.shopifyVariantId,
          steps: this._stepsForServer(finished),
          summary: finished?.summary ?? this.configuration?.summary ?? null,
          screenshot,
          imageUrl,
          locale: window.translations?.language || null,
        });

        // Alleen nodig als de server nog geen verwerkte afbeeldings-url had:
        // dan gaat het bestand als `_image` mee, zoals voorheen.
        const imageFile = result.imageUrl
          ? null
          : ((await viewer?.captureAsFile?.(1200)) ?? null);

        await addConfiguredProductToCart({
          result,
          imageFile,
          source: "logic-configurator",
          useHenkCart: true,
        });

        this._emit("configurator-complete", {
          productId: result.productId,
          variantId: result.variantId,
          created: result.created,
        });

        if (!window.HenkCart) window.location.href = "/cart";
      } catch (err) {
        adding?.abort();
        if (err.name !== "AbortError") {
          this._emit("configurator-error", { message: err.message });
          console.error("[logic-configurator] Add to cart failed:", err);
        }
      } finally {
        this._setLoading(false);
      }
    }

    /**
     * Stappen voor de server. LogicTrade is leidend voor de gekozen waarden;
     * wij vullen alleen de Shopify-metaobject-id's aan (`lt_cfg_*`), zodat de
     * ERP de keuze op id kan herleiden in plaats van op vrije tekst.
     */
    _stepsForServer(finished) {
      const sourceSteps = finished?.steps ?? this.configuration?.steps ?? [];
      return sourceSteps.map((step, index) => {
        const selectedOpt =
          step.selectedValue ??
          step.possibleValues?.find((v) => (v.key || v.value) === step.value) ??
          null;
        const optKey = selectedOpt?.key || selectedOpt?.value || step.value || "";
        const optCode = selectedOpt?.code ?? selectedOpt?.attributes?.code ?? optKey;
        const cfgMatch = this._findCfgOptionMatch?.(step, optKey, optCode) ?? null;

        return {
          id: step.id,
          code: step.code || null,
          description: step.description || step.code || String(step.id),
          value: step.value ?? optKey,
          valueDescription:
            selectedOpt?.value ?? selectedOpt?.key ?? selectedOpt?.label ?? step.valueDescription ?? "",
          optionCode: String(optCode ?? "").trim(),
          optionId: cfgMatch?.optionId ?? null,
          valueId: cfgMatch?.valueId ?? null,
          sequence: step.sequence ?? (index + 1) * 10,
          show: step.show !== false,
          enabled: step.enabled !== false,
        };
      });
    }

    reinit() {
      this.configurationGuid = null;
      this.configuration = null;
      this._activeStepIndex = 0;
      this._start();
    }
  },
);

// Re-init when Shopify section loads in theme editor
document.addEventListener("shopify:section:load", (ev) => {
  const root = ev.target || document;
  root.querySelectorAll?.("logic-configurator")?.forEach((el) => {
    if (el.reinit) el.reinit();
  });
});
